import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ban, CreditCard } from 'lucide-react';

/** GET /api/billing/account-state (server/services/billingSuspensionService). */
export interface AccountBillingState {
  status: 'ok' | 'past_due' | 'trial_ended' | 'suspended';
  kind: 'payment_failed' | 'trial_ended' | null;
  daysUntilSuspend: number | null;
  daysUntilRelease: number | null;
  suspendAt: string | null;
  releaseAt: string | null;
  numbersReleased: boolean;
  /** Ended trial still inside the billing timeline's grace window — full access. */
  trialGrace?: boolean;
  billingPath: string;
  /** The suspension policy's clock: why this account is stopped, and the date its numbers go. */
  suspension?: {
    reason: 'past_due' | 'non_payment' | 'trial_ended' | 'admin' | 'abuse' | 'security' | 'compliance';
    startedAt: string;
    releaseAt: string;
    daysUntilRelease: number;
    released: boolean;
    /** Paying lifts it; otherwise only support can. */
    selfRestorable: boolean;
  } | null;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' });

/**
 * Why the account is stopped and what happens next, from the suspension policy's
 * clock. One place, because the banner and the suspended screen must agree.
 */
export function suspensionPolicyCopy(s: AccountBillingState | null | undefined): {
  title: string;
  detail: string;
  cta: string;
  selfRestorable: boolean;
} | null {
  const c = s?.suspension;
  if (!c) return null;
  const off = 'Calls, texts and the AI receptionist are off.';
  // Once it has released there is nothing left to pay: only a new plan brings it back.
  const lapsed = c.selfRestorable && c.released;
  const title = lapsed ? 'Your subscription has been cancelled'
    : c.reason === 'trial_ended' ? 'Your trial has ended'
    : c.selfRestorable ? 'Service suspended: payment failed' : 'Account suspended';
  const cta = !c.selfRestorable ? 'Review billing'
    : lapsed || c.reason === 'trial_ended' ? 'Choose plan' : 'Pay now';
  const next = c.selfRestorable ? 'Paying restores them straight away.' : 'Contact support if you think this is a mistake.';
  const detail = c.released
    ? `${off} Your phone numbers have been released and your subscription has been cancelled.`
    : `${off} ${next} On ${shortDate(c.releaseAt)} your phone numbers will be released and your subscription cancelled.`;
  return { title, detail, cta, selfRestorable: c.selfRestorable };
}

/** Pure: the banner's words, or null when there is nothing to say. Exported for tests. */
export function billingBannerCopy(s: AccountBillingState | null | undefined): {
  tone: 'warning' | 'danger';
  title: string;
  detail: string;
  cta: string;
} | null {
  if (!s || s.status === 'ok') return null;
  const policy = suspensionPolicyCopy(s);
  if (policy) return { tone: 'danger', title: policy.title, detail: policy.detail, cta: policy.cta };
  const isTrial = s.kind === 'trial_ended';
  if (s.status === 'suspended') {
    let detail = 'Calls and texts are paused.';
    if (s.numbersReleased) detail = 'Calls and texts are paused, and your phone numbers have been released.';
    else if (s.daysUntilRelease !== null) {
      detail = s.daysUntilRelease <= 0
        ? 'Calls and texts are paused. Your numbers are being released.'
        : `Calls and texts are paused. Your numbers will be released in ${plural(s.daysUntilRelease, 'day')}.`;
    }
    return { tone: 'danger', title: 'Service suspended', detail, cta: isTrial ? 'Choose plan' : 'Update payment' };
  }
  const days = s.daysUntilSuspend ?? 0;
  const when = days <= 0 ? 'today' : `in ${plural(days, 'day')}`;
  if (s.status === 'trial_ended') {
    return { tone: 'warning', title: 'Your trial has ended', detail: `Choose a plan to keep your number — service pauses ${when}.`, cta: 'Choose plan' };
  }
  return { tone: 'warning', title: 'Payment failed', detail: `Update your payment method — service pauses ${when}.`, cta: 'Update payment' };
}

/**
 * Persistent banner for past-due, trial-ended and suspended accounts. Not
 * dismissible: it goes away when the account is paid. "Update payment" opens
 * the in-app Billing tab, which holds the Stripe portal / plan picker.
 */
export function MobileBillingBanner({ onOpenBilling }: { onOpenBilling: () => void }) {
  const { data } = useQuery<AccountBillingState>({
    queryKey: ['/api/billing/account-state'],
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
    retry: 1,
  });
  const copy = billingBannerCopy(data);
  if (!copy) return null;
  const danger = copy.tone === 'danger';
  const Icon = danger ? Ban : AlertTriangle;
  return (
    <div
      role="alert"
      className={`w-full flex items-center justify-between gap-3 px-4 md:px-6 py-2 border-b ${
        danger
          ? 'bg-red-50 border-red-100 dark:bg-red-500/10 dark:border-red-500/20'
          : 'bg-amber-50 border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20'
      }`}
      data-testid={`banner-billing-${data?.status}`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon className={`h-4 w-4 shrink-0 ${danger ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-foreground truncate">{copy.title}</div>
          <div className="text-[11px] text-muted-foreground">{copy.detail}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenBilling}
        className={`inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-md text-[12px] font-semibold text-white active:scale-[0.98] transition-transform ${
          danger ? 'bg-red-600' : 'bg-amber-600'
        }`}
        data-testid="button-billing-banner-cta"
      >
        <CreditCard className="h-3.5 w-3.5" />
        {copy.cta}
      </button>
    </div>
  );
}

export default MobileBillingBanner;
